import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import useOrderProperties from "@rightstore/Model/useOrderProperties";
import getLocalizedDate from "@rightstore/Utilities/getLocalizedDate";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    purchasedByGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    paymentMethodGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    productNameGridItem?: GridItemProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    container: {
        css: css`
            max-width: 100% !important;
            > div {
                flex-wrap: nowrap !important;
            }
        `,
    },
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 24px 0;
            color: #333333;
            font-size: 1rem;
            font-weight: 400;
        `,
    },
    pageTypeLinkGridItem: {
        width: 2,
        css: css`
            color: #333333;
            font-size: 1rem;
            font-weight: 400;
        `,
    },
    purchasedByGridItem: {
        width: 3,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 15px 23px 15px 28px;
                    `,
                    css`
                        padding: 15px 23px 15px 28px;
                    `,
                ]);
            }}
        `,
    },
    statusGridItem: {
        width: 3,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 15px 23px 15px 15px;
                    `,
                    css`
                        padding: 15px 23px 15px 15px;
                    `,
                ]);
            }}
        `,
    },
    webOrderNumberGridItem: {
        width: 2,
        css: css`
            span {
                color: #006ff8 !important;
                text-decoration: underline;
                font-size: 1rem;
                font-weight: 400;
            }
        `,
    },
    totalGridItem: {
        width: 2,
        css: css`
            text-align: right;
            justify-content: flex-end;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 15px 34px 15px 15px;
                    `,
                    css`
                        padding: 15px 34px 15px 15px;
                    `,
                ]);
            }}
        `,
    },
    paymentMethodGridItem: {
        width: 2,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 15px 34px 15px 15px;
                    `,
                    css`
                        padding: 15px 34px 15px 15px;
                    `,
                ]);
            }}
        `,
    },
    statusHeadingAndText: {
        text: {
            color: "#CA4F00",
        },
    },
    productNameGridItem: {
        width: 2,
        css: css`
            color: #333333;
            font-size: 1rem;
            font-weight: 400;
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDate({
        date: order.orderDate,
        language,
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const { placedByUserName } = useOrderProperties(order);

    const findProductName = (order: OrderModel): string | undefined => {
        if (!order || !order?.orderLines?.length) {
            return undefined;
        }

        for (let i = 0; i < order.orderLines.length; i++) {
            const currentOrderLine = order.orderLines[i];
            if (currentOrderLine.shortDescription) {
                return currentOrderLine.shortDescription;
            }
            if (currentOrderLine.description) {
                return currentOrderLine.description;
            }
        }

        return undefined;
    };

    const productName = findProductName(order);

    return (
        <GridItem className="order-summary-card-grid-item" {...styles.orderSummaryCardGridItem}>
            <GridContainer
                className="order-summary-card-container"
                {...styles.container}
                data-test-selector={`orderSummaryCard_${orderNumber}`}
            >
                {orderSettings.showWebOrderNumber && order.webOrderNumber && (
                    <GridItem className="web-order-number-grid-item" {...styles.webOrderNumberGridItem}>
                        {orderNumber && (
                            <OrderDetailPageTypeLink title={order.webOrderNumber} orderNumber={orderNumber} />
                        )}
                    </GridItem>
                )}
                {!!productName && (
                    <GridItem
                        className="page-type-link-grid-item"
                        {...styles.productNameGridItem}
                        title={productName}
                        data-test-selector="orderSummaryCard_ProduceName"
                    >
                        <SmallHeadingAndText
                            heading=""
                            text={productName}
                            extendedStyles={styles.webOrderNumberHeadingAndText}
                        />
                    </GridItem>
                )}
                <GridItem
                    className="page-type-link-grid-item"
                    {...styles.pageTypeLinkGridItem}
                    data-test-selector="orderSummaryCard_Date"
                >
                    <SmallHeadingAndText
                        heading=""
                        text={orderDateDisplay}
                        extendedStyles={styles.webOrderNumberHeadingAndText}
                    />
                </GridItem>
                <GridItem className="total-grid-item" {...styles.totalGridItem}>
                    {order.orderGrandTotalDisplay && (
                        <SmallHeadingAndText
                            heading=""
                            text={order.orderGrandTotalDisplay}
                            extendedStyles={styles.totalHeadingAndText}
                        />
                    )}
                </GridItem>
                <GridItem className="paymentMethod-grid-item" {...styles.paymentMethodGridItem}>
                    {order.terms && (
                        <SmallHeadingAndText
                            heading=""
                            text={order.terms}
                            extendedStyles={styles.totalHeadingAndText}
                        />
                    )}
                </GridItem>
                <GridItem className="status-grid-item" {...styles.statusGridItem}>
                    {order.statusDisplay && (
                        <SmallHeadingAndText
                            heading=""
                            text={order.statusDisplay}
                            extendedStyles={order.status === "Submitted" ? styles.statusHeadingAndText : undefined}
                        />
                    )}
                </GridItem>
                <GridItem className="purchasedby-grid-item" {...styles.purchasedByGridItem} title={placedByUserName}>
                    {placedByUserName != null && <SmallHeadingAndText heading="" text={placedByUserName} />}
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
